import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import {
  Briefcase, Upload, User, Mail, Phone, MapPin, Sparkles, CheckCircle2,
  Calendar, Clock, X, ArrowRight, Send, FileText, IndianRupee, Video,
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { CareerShareStrip } from './CareerShare';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';
import { INDIAN_STATES, getCitiesByState } from '../constants/locations';
import { useToast } from '../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Office-hour interview slots — kept in sync with the backend copy in the email
const OFFICE_TIME_SLOTS = [
  '10:00 AM', '11:00 AM', '12:00 PM',
  '02:00 PM', '03:00 PM', '04:00 PM', '05:00 PM',
];
const MAX_CV_MB = 5;

const tomorrow = () => {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  return d.toISOString().slice(0, 10);
};

const oneMonthAhead = () => {
  const d = new Date();
  d.setDate(d.getDate() + 45);
  return d.toISOString().slice(0, 10);
};

export const Career = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [roles, setRoles] = useState([]);
  const [form, setForm] = useState({
    name: '', email: '', phone: '',
    address: '', state: '', city: '', pincode: '',
    role_key: '', role_label: '',
    work_mode: 'in_office',
    experience_years: 0, expected_salary_annual: '',
    cover_letter: '',
  });
  const [cvFile, setCvFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(null);

  const states = useMemo(() => INDIAN_STATES || [], []);
  const cities = useMemo(() => (form.state ? getCitiesByState(form.state) : []), [form.state]);

  useEffect(() => {
    axios.get(`${API}/career/roles`)
      .then((r) => {
        const list = r.data || [];
        setRoles(list);
        const wanted = new URLSearchParams(window.location.search).get('role');
        const match = list.find((x) => x.key === wanted);
        if (match) setForm((f) => ({ ...f, role_key: match.key, role_label: match.label }));
      })
      .catch(() => setRoles([]));
  }, []);

  const canSubmit = useMemo(() => {
    if (!form.name.trim() || !form.email.trim() || !/^\d{10}$/.test(form.phone.replace(/\D/g, '').slice(-10))) return false;
    if (!form.role_key || !form.role_label) return false;
    if (!form.state || !form.city) return false;
    if (form.pincode && !/^\d{6}$/.test(form.pincode)) return false;
    if (!cvFile) return false;
    if (cvFile.size > MAX_CV_MB * 1024 * 1024) return false;
    return true;
  }, [form, cvFile]);

  const handleFile = (file) => {
    if (!file) { setCvFile(null); return; }
    if (file.size > MAX_CV_MB * 1024 * 1024) {
      toast({ title: `CV too large`, description: `Max ${MAX_CV_MB} MB — please compress and try again.`, variant: 'destructive' });
      return;
    }
    setCvFile(file);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) {
      toast({ title: 'Please complete all required fields and attach your CV.', variant: 'destructive' });
      return;
    }
    setSubmitting(true);
    try {
      const data = new FormData();
      data.append('name', form.name.trim());
      data.append('email', form.email.trim());
      data.append('phone', form.phone.replace(/\D/g, '').slice(-10));
      data.append('address', form.address.trim());
      data.append('state', form.state);
      data.append('city', form.city);
      data.append('pincode', form.pincode);
      data.append('role_key', form.role_key);
      data.append('role_label', form.role_label);
      data.append('work_mode', form.work_mode || 'in_office');
      data.append('experience_years', String(form.experience_years || 0));
      if (form.expected_salary_annual) data.append('expected_salary_annual', String(form.expected_salary_annual));
      data.append('cover_letter', form.cover_letter.trim());
      data.append('origin', window.location.origin);
      data.append('resume', cvFile);

      const res = await axios.post(`${API}/career/apply`, data, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setSubmitted({
        id: res.data.id,
        phone: form.phone.replace(/\D/g, '').slice(-10),
        role: form.role_label,
        name: form.name.trim(),
      });
      setForm({
        name: '', email: '', phone: '', address: '', state: '', city: '', pincode: '',
        role_key: '', role_label: '', work_mode: 'in_office', experience_years: 0, expected_salary_annual: '', cover_letter: '',
      });
      setCvFile(null);
    } catch (err) {
      toast({
        title: 'Could not submit application',
        description: err.response?.data?.detail || 'Please retry in a moment.',
        variant: 'destructive',
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="career" className="am-reveal py-20 bg-gradient-to-b from-slate-50 to-white">
      <div className="container mx-auto px-4">
        <div className="text-center mb-10 max-w-2xl mx-auto">
          <div className="inline-flex items-center gap-2 px-3 py-1 bg-emerald-100 border border-emerald-200 rounded-full mb-4">
            <Briefcase size={12} className="text-emerald-700" />
            <span className="text-[10px] font-bold tracking-[0.18em] uppercase text-emerald-700">Careers at Acremitra Pvt Ltd</span>
          </div>
          <h2 className="text-4xl sm:text-5xl font-bold text-slate-900 leading-tight">Build your career with us.</h2>
          <p className="text-lg text-slate-600 mt-3">Attach your CV, pick a role, and lock a video interview slot in office hours.</p>
        </div>

        {roles.length > 0 && <CareerShareStrip roles={roles} />}

        <div className="max-w-3xl mx-auto bg-white p-6 sm:p-10 rounded-2xl shadow-xl border border-slate-100" data-testid="career-form-card">
          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Role picker */}
            <div>
              <Label className="text-xs font-semibold text-slate-700 mb-1 flex items-center gap-1.5">
                Role you are applying for *
              </Label>
              <Select
                value={form.role_key}
                onValueChange={(v) => {
                  const r = roles.find((x) => x.key === v);
                  setForm((f) => ({ ...f, role_key: v, role_label: r?.label || '' }));
                }}
              >
                <SelectTrigger data-testid="career-role"><SelectValue placeholder="Select a role" /></SelectTrigger>
                <SelectContent>
                  {roles.map((r) => <SelectItem key={r.key} value={r.key}>{r.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-xs font-semibold text-slate-700 mb-1 flex items-center gap-1.5"><User size={12} className="text-emerald-700"/> Full name *</Label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Priya Sharma" data-testid="career-name" required />
              </div>
              <div>
                <Label className="text-xs font-semibold text-slate-700 mb-1 flex items-center gap-1.5"><Mail size={12} className="text-emerald-700"/> Email *</Label>
                <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="you@example.com" data-testid="career-email" required />
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-xs font-semibold text-slate-700 mb-1 flex items-center gap-1.5"><Phone size={12} className="text-emerald-700"/> Phone (10 digits) *</Label>
                <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value.replace(/\D/g, '').slice(0, 10) })} placeholder="9876543210" inputMode="numeric" data-testid="career-phone" required />
              </div>
              <div>
                <Label className="text-xs font-semibold text-slate-700 mb-1">Experience (years) *</Label>
                <Input type="number" min="0" max="40" value={form.experience_years} onChange={(e) => setForm({ ...form, experience_years: parseInt(e.target.value, 10) || 0 })} data-testid="career-experience" required />
              </div>
            </div>

            {/* Preferred work mode — feeds into HR filtering + eventual offer letter */}
            <div>
              <Label className="text-xs font-semibold text-slate-700 mb-1.5 block">Preferred work mode *</Label>
              <div className="grid grid-cols-3 gap-2" role="radiogroup" aria-label="Preferred work mode">
                {[
                  { k: 'in_office', label: 'In office', hint: 'From HQ Kolkata / regional office' },
                  { k: 'remote',    label: 'Work from home', hint: 'Fully remote, no relocation' },
                  { k: 'hybrid',    label: 'Hybrid', hint: 'Split remote + office days' },
                ].map((o) => {
                  const active = form.work_mode === o.k;
                  return (
                    <button
                      key={o.k}
                      type="button"
                      role="radio"
                      aria-checked={active}
                      onClick={() => setForm({ ...form, work_mode: o.k })}
                      className={`rounded-xl border-2 px-3 py-2 text-left transition-all ${active ? 'border-emerald-600 bg-emerald-50 ring-2 ring-emerald-200' : 'border-slate-200 bg-white hover:border-emerald-300'}`}
                      data-testid={`career-workmode-${o.k}`}
                    >
                      <div className={`text-[11px] font-black uppercase tracking-widest ${active ? 'text-emerald-700' : 'text-slate-700'}`}>{o.label}</div>
                      <div className="text-[10px] text-slate-500 mt-0.5 leading-tight">{o.hint}</div>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Address block */}
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-xs font-semibold text-slate-700 mb-1 flex items-center gap-1.5"><MapPin size={12} className="text-emerald-700"/> State *</Label>
                <Select value={form.state} onValueChange={(v) => setForm({ ...form, state: v, city: '' })}>
                  <SelectTrigger data-testid="career-state"><SelectValue placeholder="Pick your state" /></SelectTrigger>
                  <SelectContent className="max-h-[300px]">
                    {states.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs font-semibold text-slate-700 mb-1">City *</Label>
                <Select value={form.city} onValueChange={(v) => setForm({ ...form, city: v })} disabled={!form.state}>
                  <SelectTrigger data-testid="career-city"><SelectValue placeholder={form.state ? 'Pick city' : 'Pick state first'} /></SelectTrigger>
                  <SelectContent className="max-h-[300px]">
                    {cities.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-xs font-semibold text-slate-700 mb-1">Pincode</Label>
                <Input value={form.pincode} onChange={(e) => setForm({ ...form, pincode: e.target.value.replace(/\D/g, '').slice(0, 6) })} placeholder="e.g. 700016" inputMode="numeric" data-testid="career-pincode" />
              </div>
              <div>
                <Label className="text-xs font-semibold text-slate-700 mb-1 flex items-center gap-1.5"><IndianRupee size={12} className="text-emerald-700" /> Expected salary (₹ / year)</Label>
                <Input type="number" min="0" value={form.expected_salary_annual} onChange={(e) => setForm({ ...form, expected_salary_annual: e.target.value })} placeholder="e.g. 600000" data-testid="career-expected-salary" />
              </div>
            </div>
            <div>
              <Label className="text-xs font-semibold text-slate-700 mb-1">Full address</Label>
              <Textarea rows={2} value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} placeholder="House / flat number, street, locality" data-testid="career-address" />
            </div>

            {/* CV upload */}
            <div>
              <Label className="text-xs font-semibold text-slate-700 mb-1 flex items-center gap-1.5"><Upload size={12} className="text-emerald-700"/> Attach your CV / resume * (max {MAX_CV_MB} MB)</Label>
              <label htmlFor="career-cv" className="block cursor-pointer rounded-xl border-2 border-dashed border-slate-300 hover:border-emerald-500 hover:bg-emerald-50/30 transition-colors p-5 text-center" data-testid="career-cv-dropzone">
                {cvFile ? (
                  <div className="flex items-center justify-center gap-3 text-emerald-700">
                    <FileText size={20} />
                    <div className="text-left">
                      <div className="font-semibold text-sm">{cvFile.name}</div>
                      <div className="text-xs text-slate-500">{(cvFile.size / 1024 / 1024).toFixed(2)} MB · Click to replace</div>
                    </div>
                  </div>
                ) : (
                  <div className="flex flex-col items-center gap-1 text-slate-500">
                    <Upload size={22} className="text-emerald-600" />
                    <div className="font-semibold text-sm text-slate-700">Click to upload your CV</div>
                    <div className="text-xs">Any file type · Max {MAX_CV_MB} MB</div>
                  </div>
                )}
                <input id="career-cv" type="file" className="hidden" onChange={(e) => handleFile(e.target.files?.[0] || null)} data-testid="career-cv-input" />
              </label>
            </div>

            <div>
              <Label className="text-xs font-semibold text-slate-700 mb-1">Why do you want to join Acremitra? (optional)</Label>
              <Textarea rows={3} value={form.cover_letter} onChange={(e) => setForm({ ...form, cover_letter: e.target.value })} placeholder="A few lines about your motivation and past achievements…" data-testid="career-cover" />
            </div>

            <button type="submit" disabled={!canSubmit || submitting} className="btn-am-primary w-full py-4 text-base" data-testid="career-submit">
              {submitting ? (
                <>
                  <span>Submitting…</span>
                </>
              ) : (
                <>
                  <Send size={16} />
                  <span>Submit Application</span>
                  <ArrowRight size={16} />
                </>
              )}
            </button>
          </form>
        </div>
      </div>

      {submitted && (
        <InterviewSchedulerModal
          application={submitted}
          onClose={() => setSubmitted(null)}
          onScheduled={(scheduled) => {
            toast({
              title: 'Interview slot locked!',
              description: 'Meet link + password have been emailed to you.',
            });
            const trackUrl = `/track?id=${submitted.id}&phone=${submitted.phone}`;
            setSubmitted(null);
            navigate(trackUrl);
          }}
        />
      )}
    </section>
  );
};

// ---------- Interview scheduler modal (post-submit) ----------

const InterviewSchedulerModal = ({ application, onClose, onScheduled }) => {
  const { toast } = useToast();
  const [date, setDate] = useState(tomorrow());
  const [timeSlot, setTimeSlot] = useState(OFFICE_TIME_SLOTS[0]);
  const [saving, setSaving] = useState(false);
  const [scheduled, setScheduled] = useState(null); // { meet_link, meet_password, ... }

  const isSunday = (yyyyMmDd) => {
    const [y, m, d] = yyyyMmDd.split('-').map(Number);
    return new Date(y, m - 1, d).getDay() === 0;
  };

  const submit = async () => {
    if (isSunday(date)) {
      toast({ title: 'We are closed on Sundays. Pick another day.', variant: 'destructive' });
      return;
    }
    setSaving(true);
    try {
      const { data } = await axios.patch(`${API}/career/${application.id}/schedule`, {
        interview_date: date,
        interview_time: timeSlot,
      });
      setScheduled(data);
    } catch (err) {
      toast({
        title: 'Could not schedule interview',
        description: err.response?.data?.detail || 'Please retry in a moment.',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  return createPortal(
    <div className="fixed inset-0 bg-slate-900/70 z-[1000] flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl max-w-lg w-full p-6 sm:p-8 relative shadow-2xl max-h-[92vh] overflow-y-auto" data-testid="interview-scheduler-modal">
        <button onClick={onClose} className="absolute top-4 right-4 text-slate-400 hover:text-slate-700" data-testid="interview-modal-close">
          <X size={22} />
        </button>

        {!scheduled ? (
          <>
            <div className="text-center mb-6">
              <div className="mx-auto w-14 h-14 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center mb-3">
                <CheckCircle2 size={26} />
              </div>
              <h2 className="text-2xl font-bold text-slate-900">Application received!</h2>
              <p className="text-slate-600 text-sm mt-1">
                Thanks {application.name?.split(' ')[0] || 'there'} — pick an interview slot in our office hours.
              </p>
            </div>

            <div className="rounded-xl bg-emerald-50 border border-emerald-200 px-4 py-3 mb-5 text-sm text-emerald-800">
              <div className="text-[10px] uppercase tracking-widest font-bold text-emerald-700">Applied For</div>
              <div className="font-semibold text-slate-900">{application.role}</div>
              <div className="text-xs text-slate-500 mt-1">Application ID · #{application.id?.slice(0, 8).toUpperCase()}</div>
            </div>

            <div className="space-y-4">
              <div>
                <Label className="text-xs font-semibold text-slate-700 mb-1 flex items-center gap-1.5"><Calendar size={12} className="text-emerald-700"/> Date *</Label>
                <Input type="date" value={date} min={tomorrow()} max={oneMonthAhead()} onChange={(e) => setDate(e.target.value)} data-testid="interview-date" />
                {isSunday(date) && <p className="text-xs text-red-600 mt-1">We are closed on Sundays.</p>}
              </div>
              <div>
                <Label className="text-xs font-semibold text-slate-700 mb-1 flex items-center gap-1.5"><Clock size={12} className="text-emerald-700"/> Time slot (office hours 10 AM – 6 PM) *</Label>
                <Select value={timeSlot} onValueChange={setTimeSlot}>
                  <SelectTrigger data-testid="interview-time"><SelectValue /></SelectTrigger>
                  <SelectContent className="z-[1100]">
                    {OFFICE_TIME_SLOTS.map((t) => (<SelectItem key={t} value={t}>{t}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="mt-4 rounded-lg bg-amber-50 border border-amber-200 px-3 py-2.5 text-[11px] text-amber-800 leading-snug">
              <strong>Note:</strong> this is the <strong>1st round video interview</strong> (via Jitsi Meet — no signup required, just click and enter your name). If selected, the <strong>2nd round will be at our office</strong>.
            </div>

            <div className="mt-6 flex gap-3">
              <Button variant="outline" className="flex-1" onClick={onClose} disabled={saving} data-testid="interview-skip">Skip for now</Button>
              <Button className="flex-1 bg-emerald-700 hover:bg-emerald-800 text-white font-bold" onClick={submit} disabled={saving || isSunday(date)} data-testid="interview-confirm">
                {saving ? 'Locking…' : (<>Confirm Slot <ArrowRight size={14} className="ml-1.5" /></>)}
              </Button>
            </div>

            <p className="text-[11px] text-slate-500 text-center mt-4">
              You&apos;ll get a confirmation email + a <strong>/track</strong> link to follow your application status.
            </p>
          </>
        ) : (
          <>
            <div className="text-center mb-5">
              <div className="mx-auto w-14 h-14 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center mb-3">
                <Video size={26} />
              </div>
              <h2 className="text-2xl font-bold text-slate-900">You&apos;re all set!</h2>
              <p className="text-slate-600 text-sm mt-1">1st round video interview — click the button below at your slot to join.</p>
            </div>

            <div className="rounded-xl bg-emerald-50 border border-emerald-200 px-4 py-3 text-sm mb-4">
              <div className="text-[10px] uppercase tracking-widest font-bold text-emerald-700">Interview Slot</div>
              <div className="font-semibold text-slate-900 text-lg">{scheduled.interview_date} · {scheduled.interview_time}</div>
            </div>

            <div className="rounded-xl border-2 border-dashed border-emerald-500 bg-emerald-50/70 p-4 mb-4" data-testid="interview-meet-block">
              <div className="flex items-center justify-between mb-2">
                <div className="text-[10px] font-bold tracking-widest uppercase text-emerald-700">Join · Jitsi Meet</div>
                <div className="text-[10px] font-semibold text-emerald-700">No signup required</div>
              </div>
              <a
                href={scheduled.meet_link}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-emerald-700 hover:bg-emerald-800 text-white font-bold px-5 py-2.5 rounded-full text-sm transition-colors"
                data-testid="interview-join-btn"
              >
                <Video size={14} /> Click to join meeting
              </a>
              <div className="text-emerald-800 font-mono text-[11px] break-all mt-3 opacity-80">
                {scheduled.meet_link}
              </div>
              <div className="mt-3 text-[10px] font-bold tracking-widest uppercase text-emerald-700">Reference Code</div>
              <div className="text-emerald-900 font-mono font-black text-lg tracking-widest mt-1">{scheduled.meet_password}</div>
              <p className="text-[11px] text-emerald-700 mt-3">Just click, enter your name and join — no login or download. If HR emails an updated link, use the latest one.</p>
            </div>

            <div className="rounded-lg bg-amber-50 border border-amber-200 px-3 py-2.5 text-[11px] text-amber-800 mb-4">
              This is your <strong>1st round interview</strong>. If selected, the <strong>2nd round will be at our office</strong>.
            </div>

            <div className="flex gap-3">
              <Button variant="outline" className="flex-1" onClick={onClose}>Close</Button>
              <Button className="flex-1 bg-emerald-700 hover:bg-emerald-800 text-white" onClick={() => onScheduled?.(scheduled)} data-testid="interview-track-btn">
                Track application <ArrowRight size={14} className="ml-1.5" />
              </Button>
            </div>
          </>
        )}
      </div>
    </div>,
    document.body,
  );
};

export default Career;
